import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";

/**
 * Camera-based barcode / QR scanner. Calls onDetected(code) once a code is
 * successfully decoded, then closes the modal. Also offers a manual-entry
 * fallback for cases where the camera can't focus on the barcode.
 */
export default function BarcodeScanner({ open, onClose, onDetected }) {
  const containerId = "barcode-reader";
  const scannerRef = useRef(null);
  const [manual, setManual] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    setError("");
    setStarting(true);
    const safeStop = async (s) => {
      if (!s) return;
      try {
        const state = typeof s.getState === "function" ? s.getState() : null;
        // 2 = SCANNING per Html5QrcodeScannerState; only stop if actually running
        if (state && state !== 2) return;
        await s.stop();
      } catch (err) { console.debug("Scanner stop ignored", err); }
    };
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            if (stopped) return;
            stopped = true;
            safeStop(scanner).finally(() => {
              scannerRef.current = null;
              onDetected(decoded);
            });
          },
          () => {}
        );
      } catch (e) {
        setError(e?.message || "Could not open camera. Grant permission or use manual entry.");
      } finally {
        setStarting(false);
      }
    };
    const t = setTimeout(start, 100);
    return () => {
      clearTimeout(t);
      stopped = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      safeStop(s);
    };
  }, [open, onDetected]);

  const submitManual = (e) => {
    e.preventDefault();
    const v = manual.trim();
    if (!v) return toast.error("Enter a barcode or SKU");
    onDetected(v);
    setManual("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Scan a barcode</DialogTitle>
          <DialogDescription className="sr-only">Point the camera at a book's barcode or enter the code manually.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-muted-foreground">
            Point your camera at a book's barcode (ISBN) or SKU code.
          </div>
          <div className="rounded-sm overflow-hidden bg-black aspect-video relative" data-testid="barcode-video-wrap">
            <div id={containerId} className="w-full h-full"/>
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-white/80">Starting camera…</div>
            )}
          </div>
          {error && <div className="text-xs text-destructive" data-testid="barcode-error">{error}</div>}
          <form onSubmit={submitManual} className="space-y-2 pt-2 border-t border-border">
            <Label className="text-xs">Or enter manually</Label>
            <div className="flex gap-2">
              <Input value={manual} onChange={(e)=>setManual(e.target.value)} placeholder="ISBN or SKU" data-testid="barcode-manual-input"/>
              <Button type="submit" data-testid="barcode-manual-submit">Use</Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
